/**
 * CNESDetailModal Component - Hospital detail modal
 */
import React from 'react';
import { Building2, MapPin, Phone, X } from 'lucide-react';
import type { Hospital } from '../../hooks/useCNESConsulta';

interface CNESDetailModalProps {
  hospital: Hospital;
  onClose: () => void;
}

export const CNESDetailModal: React.FC<CNESDetailModalProps> = ({
  hospital,
  onClose,
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="bg-green-600 text-white p-6 rounded-t-xl flex justify-between items-start">
          <div>
            <h3 className="text-2xl font-bold mb-2">
              {hospital.nome.replace(/^http:\/\/[^\s]+\s*/, '')}
            </h3>
            <p className="text-green-100">{hospital.razaoSocial}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-green-700 rounded-lg p-2 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          {/* Identification */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-green-600" />
              Identificação
            </h4>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">CNES:</span>
                <span className="ml-2 text-gray-900">{hospital.cnes}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">CNPJ:</span>
                <span className="ml-2 text-gray-900">{hospital.cnpj}</span>
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-green-600" />
              Endereço
            </h4>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="md:col-span-2">
                <span className="font-medium text-gray-700">Logradouro:</span>
                <span className="ml-2 text-gray-900">{hospital.endereco}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Bairro:</span>
                <span className="ml-2 text-gray-900">{hospital.bairro}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">CEP:</span>
                <span className="ml-2 text-gray-900">{hospital.cep}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Cidade:</span>
                <span className="ml-2 text-gray-900">{hospital.cidade}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Estado:</span>
                <span className="ml-2 text-gray-900">{hospital.estado}</span>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Phone className="w-5 h-5 text-green-600" />
              Contato
            </h4>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Telefone:</span>
                <span className="ml-2 text-gray-900">{hospital.telefone}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Email:</span>
                <span className="ml-2 text-gray-900">{hospital.email}</span>
              </div>
            </div>
          </div>

          {/* Administrative Info */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              Informações Administrativas
            </h4>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Tipo:</span>
                <div className="mt-1 px-3 py-2 bg-gray-100 rounded-lg text-gray-900">
                  {hospital.tipoEstabelecimento}
                </div>
              </div>
              <div>
                <span className="font-medium text-gray-700">Gestão:</span>
                <div className="mt-1 px-3 py-2 bg-gray-100 rounded-lg text-gray-900">
                  {hospital.gestao}
                </div>
              </div>
              <div>
                <span className="font-medium text-gray-700">Leitos:</span>
                <div className="mt-1 px-3 py-2 bg-blue-100 text-blue-800 rounded-lg font-semibold text-center">
                  {hospital.leitos}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="bg-gray-50 p-4 rounded-b-xl flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};
